import { mergeAttributes } from "@tiptap/core";
import { Table } from "@tiptap/extension-table";
import { isInTable, selectedRect, tableNodeTypes } from "@tiptap/pm/tables";

const tableStyle = "border-collapse: collapse;"
  + "table-layout: auto;"
  + "overflow: auto;"
  + "margin: 0.3em 0;";

function isHeaderEnabled(type, rect, types) {
  const cellPositions = rect.map.cellsInRect({
    left: 0,
    top: 0,
    right: type === "row" ? rect.map.width : 1,
    bottom: type === "column" ? rect.map.height : 1,
  });

  return cellPositions.every((pos) => rect.table.nodeAt(pos)?.type === types.header_cell);
}

/**
 * Toggles a header row/column exactly as @tiptap/extension-table's own toggleHeaderRow/Column do
 * (same cell-selection logic from @tiptap/pm/tables, including which row/column is targeted and the
 * corner-cell exclusion when the other dimension's header is already active), plus bolds/un-bolds
 * each affected cell's existing content to match. table-header.js separately bolds newly typed text
 * as it's inserted, but has no reason to touch a cell's pre-existing text — that's this command's
 * job, exactly once, at the moment a cell's header status actually changes.
 * @param {"row" | "column"} type which dimension to toggle
 * @returns {Function} a Tiptap command
 */
function toggleHeaderAndBold(type) {
  return ({ state, dispatch }) => {
    if (!isInTable(state)) {
      return false;
    }

    if (dispatch) {
      const types = tableNodeTypes(state.schema);
      const boldMarkType = state.schema.marks.bold;
      const rect = selectedRect(state);
      const { tr } = state;

      const isHeaderRowEnabled = isHeaderEnabled("row", rect, types);
      const isHeaderColumnEnabled = isHeaderEnabled("column", rect, types);
      const selectionStartsAt = (type === "column" ? isHeaderRowEnabled : isHeaderColumnEnabled) ? 1 : 0;
      const isUnbolding = type === "column" ? isHeaderColumnEnabled : isHeaderRowEnabled;
      const newCellType = isUnbolding ? types.cell : types.header_cell;

      const cellsRect = type === "column"
        ? {
          left: 0, top: selectionStartsAt, right: 1, bottom: rect.map.height,
        }
        : {
          left: selectionStartsAt, top: 0, right: rect.map.width, bottom: 1,
        };

      rect.map.cellsInRect(cellsRect).forEach((relativeCellPos) => {
        const cellPos = relativeCellPos + rect.tableStart;
        const cell = tr.doc.nodeAt(cellPos);

        if (!cell) {
          return;
        }

        tr.setNodeMarkup(cellPos, newCellType, cell.attrs);

        const contentFrom = cellPos + 1;
        const contentTo = cellPos + cell.nodeSize - 1;

        if (isUnbolding) {
          tr.removeMark(contentFrom, contentTo, boldMarkType);
        } else {
          tr.addMark(contentFrom, contentTo, boldMarkType.create());
        }
      });

      dispatch(tr);
    }

    return true;
  };
}

/**
 * Modified from @tiptap/extension-table to parse/render with element level border style attributes. FOX does not
 * currently apply any style to tables in its HTML emails.
 */
export default Table.configure({
  resizable: false,
  allowTableNodeSelection: true,
  lastColumnResizable: false,
}).extend({
  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.goToNextCell(),
      "Shift-Tab": () => this.editor.commands.goToPreviousCell(),
    };
  },
  addCommands() {
    return {
      ...this.parent(),
      toggleHeaderRow: () => toggleHeaderAndBold("row"),
      toggleHeaderColumn: () => toggleHeaderAndBold("column"),
    };
  },
  /*
    This custom render method:
    - Removes `<colgroup>` from the core tiptap extension for FOX compatibility
    - Adds table styling inline (FOX emails are essentially unstyled)
    - Adds `border="1"` so all the cells have borders (FOX disallows styling of th/td elements)
   */
  renderHTML({ HTMLAttributes }) {
    return [
      "table",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { style: tableStyle, border: "1" }),
      ["tbody", 0],
    ];
  },
});
